"use client";

import { useMemo } from "react";
import type { StationSummary } from "@/hooks/useStationHubData";
import { useHub, useHubDensity } from "./hub-context";
import { textCompare } from "@/lib/utils/text-compare";
import { HubEmptyState } from "./HubEmptyState";
import {
  HUB_GUTTER,
  HubCell,
  hubListClass,
  SectionHeader,
  SectionHeaderSkeleton,
  StationRowSkeleton,
} from "./hub-ui";
import { CampingStationRow } from "./CampingStationRow";
import { StationRow } from "./StationRow";
import { useWindowedCount } from "./useWindowedCount";

/** 상세를 기다리는 동안 그리는 스켈레톤 행 수 상한 */
const SKELETON_LIMIT = 12;

type ListItem =
  | { kind: "header"; key: string; title: string }
  | { kind: "row"; key: string; station: StationSummary };

/**
 * 지점 목록 — 지점 행. 목록 위 검색 입력은 뺐다(09-28) —
 * 지역은 상단 검색바와 지역·명소 알약으로 고른다.
 *
 * 정렬(가까운 순 · 지역별)은 허브가 정하고 칩도 허브 목록 머리 둘째 줄에 있다(10-13,
 * [지점 | 차종] 토글과 한 줄). 기준점이 없으면 거리 정렬은 의미가 없어 지역별로만 묶는다.
 *
 * 목록은 24행씩 창으로 잘라 그린다(수백 행을 한꺼번에 서버 렌더하지 않는다).
 */
export function NearbySheet({
  stations,
  sort,
}: {
  stations: StationSummary[];
  sort: "nearest" | "area";
}) {
  const {
    kind,
    t,
    locale,
    data,
    open,
    defaultNearby,
    originIsMapCenter,
    rawDistanceKm,
    landmark,
    landmarkLabel,
  } = useHub();
  // 데스크톱 목록 열에서는 2열 카드 그리드(09-23 A안).
  const wide = useHubDensity() === "wide";
  const spanClass = wide ? "col-span-2" : undefined;

  const byDistance = defaultNearby && sort === "nearest";

  // 도도부현 묶음·행 내용은 모두 지점 상세가 와야 정해진다. 오기 전에는
  // 도시명으로 흉내 내지 않고 스켈레톤으로 자리만 잡는다(높이 동일 → 튐 없음).
  const awaitingDetails = data.loadingDetails || data.details.size === 0;

  const items = useMemo<ListItem[]>(() => {
    if (awaitingDetails) return [];
    // 이름 정렬은 페이지 로케일 고정 — 서버 HTML 과 하이드레이션이 같은 순서(10-15).
    const compare = textCompare(locale);

    if (byDistance) {
      return stations
        .map((station) => ({
          station,
          km: rawDistanceKm(station.lat, station.lng),
        }))
        // 상한 없이 전부 — 목록은 useWindowedCount 가 24행씩 이어 붙인다.
        // (초안 §3-1 의 "가까운 12곳"은 헤더의 "지점 N곳"과 어긋나 09-18 폐기.)
        .sort((a, b) => a.km - b.km)
        .map((entry) => ({
          kind: "row" as const,
          key: entry.station.id,
          station: entry.station,
        }));
    }

    // 도도부현(없으면 도시) 섹션 — 아코디언 없이 평평한 헤더 + 행.
    // 묶음 이름도 상세에서만 온다(도시명 폴백 금지). 이름을 못 얻은 묶음은 맨 뒤로.
    const groups = new Map<string, StationSummary[]>();
    for (const station of stations) {
      const detail = data.details.get(station.id);
      const key = detail?.stateProvince || detail?.city || "";
      const list = groups.get(key);
      if (list) list.push(station);
      else groups.set(key, [station]);
    }

    const areas = [...groups.entries()]
      .map(([name, list]) => ({
        name,
        items: [...list].sort((a, b) => {
          const an = data.details.get(a.id)?.name ?? "";
          const bn = data.details.get(b.id)?.name ?? "";
          return compare(an, bn);
        }),
      }))
      .sort((a, b) => {
        if (!a.name !== !b.name) return a.name ? -1 : 1;
        return b.items.length - a.items.length || compare(a.name, b.name);
      });

    const flat: ListItem[] = [];
    for (const area of areas) {
      flat.push({
        kind: "header",
        key: `h:${area.name || "pending"}`,
        title: area.name ? `${area.name} · ${area.items.length}` : "",
      });
      for (const station of area.items) {
        flat.push({ kind: "row", key: station.id, station });
      }
    }
    return flat;
  }, [awaitingDetails, byDistance, stations, data.details, rawDistanceKm, locale]);

  const { visible, hasMore, attachSentinel } = useWindowedCount(items.length);

  return (
    <div className="pb-8">
      {byDistance && (originIsMapCenter || landmark) && !awaitingDetails ? (
        <p
          className={`${HUB_GUTTER} mt-3 text-[12px] text-gray-500 mapShow:text-sm`}
        >
          {landmark
            ? t("hub.nearLandmark", { name: landmarkLabel })
            : t("hub.nearMapCenter")}
        </p>
      ) : null}

      {stations.length === 0 ? (
        // 원인(필터 축 · 지도 영역 범위)에 맞춘 그림 + 문구 + 버튼(09-29)
        <HubEmptyState />
      ) : awaitingDetails ? (
        <div
          className={`mt-2 ${hubListClass(
            wide,
            // 선 대신 여백으로 구분(09-29) — 행 자체의 py 가 간격이 된다
            "flex flex-col gap-1",
            "grid grid-cols-2 items-start gap-x-3 gap-y-2",
          )}`}
        >
          {!byDistance ? <SectionHeaderSkeleton className={spanClass} /> : null}
          {Array.from({
            length: Math.min(stations.length, SKELETON_LIMIT),
          }).map((_, index) => (
            <HubCell key={index} wide={wide} bare>
              <StationRowSkeleton />
            </HubCell>
          ))}
        </div>
      ) : (
        <div
          className={`mt-2 ${hubListClass(
            wide,
            // 선 대신 여백으로 구분(09-29) — 행 자체의 py 가 간격이 된다
            "flex flex-col gap-1",
            "grid grid-cols-2 items-start gap-x-3 gap-y-2",
          )}`}
        >
          {items.slice(0, visible).map((item) =>
            item.kind === "header" ? (
              item.title ? (
                <SectionHeader
                  key={item.key}
                  title={item.title}
                  className={spanClass}
                />
              ) : (
                <SectionHeaderSkeleton key={item.key} className={spanClass} />
              )
            ) : (
              <HubCell key={item.key} wide={wide} bare>
                {kind === "camping" ? (
                  <CampingStationRow
                    station={item.station}
                    onClick={() =>
                      open({ kind: "bikes", stationId: item.station.id })
                    }
                  />
                ) : (
                  <StationRow
                    station={item.station}
                    onClick={() =>
                      open({ kind: "bikes", stationId: item.station.id })
                    }
                  />
                )}
              </HubCell>
            ),
          )}
          {hasMore ? (
            <div ref={attachSentinel} className={`h-8 ${spanClass ?? ""}`} />
          ) : null}
        </div>
      )}
    </div>
  );
}
